// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useMemo } from 'react';
import './PropertyField.scss';
import { useTranslation } from 'react-i18next';

import { doc, collection, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../../context/firebase';

import PropertyField from './PropertyField';

import supplier_icon from '../../assets/icons/white/delivery.png';
import { SUPPLIER_DETAILS_COLLECTION_PATH, SUPPLIERS_DOCUMENT_PATH } from '../../shared_components/utils/FirebaseUtils';
import { useAuthStore } from '../../stores/authStore';
import CommonModal from '../../shared_components/CommonModal/CommonModal';
import { useGetDocument } from '../../shared_components/hooks/useGetDocument';
import { useSetDocument } from '../../shared_components/hooks/useSetDocument';

export default function SupplierPropertyField({ value, handleDeliveryFieldChange, editable = true }) {
  const { t } = useTranslation();
  const currentUserClientId = useAuthStore((state) => state.currentUser?.company?.company);
  const [showAddOptionModal, setShowAddOptionModal] = React.useState(false);
  const supplierCodeRef = React.useRef(null);
  const supplierNameRef = React.useRef(null);

  // Fetch the supplier labelset from the database
  const supplierLabelsetRef = doc(firestore, SUPPLIERS_DOCUMENT_PATH(currentUserClientId));
  const supplierDetailsRef = doc(collection(firestore, SUPPLIER_DETAILS_COLLECTION_PATH(currentUserClientId)));
  const { data: supplierLabelsetData } = useGetDocument(supplierLabelsetRef, { withSnapshot: true });
  const { setDocument: setSupplierDetails } = useSetDocument(supplierDetailsRef);

  // Derive options from labelset data
  const options = useMemo(() => {
    if (!supplierLabelsetData?.summary) return {};

    const newOptions = {};
    Object.entries(supplierLabelsetData.summary).forEach(([key, label]) => {
      if (label?.supplier_code) {
        newOptions[key] = `${label.supplier_code} ${label.display_name}`;
      } else {
        newOptions[key] = label.display_name;
      }
    });
    return newOptions;
  }, [supplierLabelsetData, value]);

  async function addOption() {
    /* Add a new supplier to the supplier labelset */

    // Check that the supplier has a name
    if (!supplierNameRef.current.value || supplierNameRef.current.value.length === 0) {
      setShowAddOptionModal(false);
      return;
    }

    const newSupplierData = {
      display_name: supplierNameRef.current.value,
      created_at: serverTimestamp(),
    };

    if (supplierCodeRef.current.value && supplierCodeRef.current.value.length > 0) {
      newSupplierData.supplier_code = supplierCodeRef.current.value;
    }

    await setSupplierDetails(newSupplierData);

    // Change delivery supplier to the new supplier
    handleDeliveryFieldChange({ target: { name: 'supplier_id', value: supplierDetailsRef.id } });

    setShowAddOptionModal(false);
  }

  return (
    <>
      <PropertyField
        icon={supplier_icon}
        title={t('property_supplier')}
        value={value}
        name={'supplier_id'}
        type='select-extendible-custom'
        options={options}
        handleChange={handleDeliveryFieldChange}
        onAddOption={() => {
          setShowAddOptionModal(true);
        }}
        editable={editable}
      />

      <CommonModal
        show={showAddOptionModal}
        title={t('property_field_extendable_new')}
        confirmText={t('general_add')}
        cancelText={t('general_cancel')}
        onConfirm={addOption}
        onCancel={() => setShowAddOptionModal(false)}
        theme='dark'
      >
        <div className='mb-3'>
          <label htmlFor='supplierId'>ID</label>
          <input
            type='text'
            id='supplierId'
            ref={supplierCodeRef}
            className='box-border w-full outline-none text-white p-1.5 rounded-md bg-blue-400/20 border border-blue-400 focus:ring-1 focus:ring-blue-500 transition'
          />
        </div>
        <div>
          <label htmlFor='supplierName'>Name</label>
          <input
            type='text'
            id='supplierName'
            ref={supplierNameRef}
            className='box-border w-full outline-none text-white p-1.5 rounded-md bg-blue-400/20 border border-blue-400 focus:ring-1 focus:ring-blue-500 transition'
          />
        </div>
      </CommonModal>
    </>
  );
}
